import React from 'react';
import { useTheme } from '../../ThemeContext';
import { detectDriver } from '../../utils/driverDetection';

interface SourceConnectionFormProps {
  sourceType: 'jdbc' | 'excel_directory' | 'csv_directory' | 'parquet_directory' | 'azure_blob_storage';
  formData: any;
  onFormDataChange: (updates: any) => void;
  onUrlChange: (value: string) => void;
  required?: boolean;
}

export const SourceConnectionForm: React.FC<SourceConnectionFormProps> = ({
  sourceType,
  formData,
  onFormDataChange,
  onUrlChange,
  required = false,
}) => {
  const { theme } = useTheme();
  const sourceSuggestion = sourceType === 'jdbc' ? detectDriver(formData.sourceUrl || '') : null;

  const applySourceDetected = () => {
    if (sourceSuggestion) {
      onFormDataChange({
        sourceDriver: sourceSuggestion.driver,
        sourceDriverUrl: sourceSuggestion.driver_url
      });
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
          Source Type
        </label>
        <select
          value={sourceType}
          onChange={(e) => onFormDataChange({ sourceType: e.target.value as any })}
          style={{
            width: '100%',
            padding: 8,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: 6,
            fontSize: 13,
            background: theme.inputBackground,
            color: theme.text
          }}
        >
          <option value="jdbc">JDBC</option>
          <option value="excel_directory">Excel directory</option>
          <option value="csv_directory">CSV directory</option>
          <option value="parquet_directory">Parquet directory</option>
          <option value="azure_blob_storage">Azure Blob Storage</option>
        </select>
      </div>

      {sourceType === 'jdbc' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              JDBC URL {required && '*'}
            </label>
            <input
              type="text"
              required={required && sourceType === 'jdbc'}
              value={formData.sourceUrl || ''}
              onChange={(e) => onUrlChange(e.target.value)}
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
            {sourceSuggestion && (
              <div style={{ marginTop: 6, fontSize: 12, color: theme.textSecondary }}>
                Detected driver: <code style={{ background: theme.codeBackground, padding: '2px 6px', borderRadius: 4, color: theme.text }}>
                  {sourceSuggestion.driver}
                </code> —
                <a href={sourceSuggestion.driver_url} target="_blank" rel="noreferrer" style={{ color: theme.accent }}>
                  Download driver
                </a>
                {formData.sourceDriver !== sourceSuggestion.driver && (
                  <button
                    type="button"
                    onClick={applySourceDetected}
                    style={{
                      marginLeft: 8,
                      padding: '2px 8px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      background: theme.cardBackground,
                      cursor: 'pointer',
                      color: theme.text
                    }}
                  >
                    Use detected
                  </button>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Table {required && '*'}
              </label>
              <input
                type="text"
                required={required && sourceType === 'jdbc'}
                value={formData.sourceTable || ''}
                onChange={(e) => onFormDataChange({ sourceTable: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                User {required && '*'}
              </label>
              <input
                type="text"
                required={required && sourceType === 'jdbc'}
                value={formData.sourceUser || ''}
                onChange={(e) => onFormDataChange({ sourceUser: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Password
            </label>
            <input
              type="password"
              value={formData.sourcePassword || ''}
              onChange={(e) => onFormDataChange({ sourcePassword: e.target.value })}
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
          </div>
        </>
      )}

      {sourceType === 'excel_directory' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Directory Path *
              </label>
              <input
                type="text"
                value={formData.excelPath || ''}
                onChange={(e) => onFormDataChange({ excelPath: e.target.value })}
                placeholder="C:\\ingest\\inbox"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Pattern
              </label>
              <input
                type="text"
                value={formData.excelPattern || ''}
                onChange={(e) => onFormDataChange({ excelPattern: e.target.value })}
                placeholder="*.xlsx"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Sheet name
              </label>
              <input
                type="text"
                value={formData.excelSheet || ''}
                onChange={(e) => onFormDataChange({ excelSheet: e.target.value })}
                placeholder="Sheet1"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Data Address
              </label>
              <input
                type="text"
                value={formData.excelDataAddress || ''}
                onChange={(e) => onFormDataChange({ excelDataAddress: e.target.value })}
                placeholder="'Sheet1'!A1"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.excelHeader || false}
                onChange={(e) => onFormDataChange({ excelHeader: e.target.checked })}
              />
              Header row
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.excelInferSchema || false}
                onChange={(e) => onFormDataChange({ excelInferSchema: e.target.checked })}
              />
              Infer schema
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.excelRecursive || false}
                onChange={(e) => onFormDataChange({ excelRecursive: e.target.checked })}
              />
              Recursive
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.excelSkipTransformations || false}
                onChange={(e) => onFormDataChange({ excelSkipTransformations: e.target.checked })}
              />
              Skip transformations
            </label>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.excelPureCopy || false}
                onChange={(e) => onFormDataChange({ excelPureCopy: e.target.checked })}
              />
              <span style={{ fontWeight: 600 }}>Pure Copy Mode</span>
            </label>
            <div style={{ marginTop: 4, fontSize: 12, color: theme.textSecondary, marginLeft: 28 }}>
              Copy entire Excel file(s) as-is to Azure Blob Storage without any data processing. 
              Requires target to be Azure Blob with Excel format.
            </div>
          </div>
        </>
      )}

      {sourceType === 'csv_directory' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Directory Path *
              </label>
              <input
                type="text"
                value={formData.csvPath || ''}
                onChange={(e) => onFormDataChange({ csvPath: e.target.value })}
                placeholder="C:\\ingest\\csv"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Pattern
              </label>
              <input
                type="text"
                value={formData.csvPattern || ''}
                onChange={(e) => onFormDataChange({ csvPattern: e.target.value })}
                placeholder="*.csv"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.csvHeader || false}
                onChange={(e) => onFormDataChange({ csvHeader: e.target.checked })}
              />
              Header row
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.csvInferSchema || false}
                onChange={(e) => onFormDataChange({ csvInferSchema: e.target.checked })}
              />
              Infer schema
            </label>
          </div>
        </>
      )}

      {sourceType === 'parquet_directory' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Directory Path *
              </label>
              <input
                type="text"
                value={formData.parquetPath || ''}
                onChange={(e) => onFormDataChange({ parquetPath: e.target.value })}
                placeholder="/app/data/source/parquet"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Pattern
              </label>
              <input
                type="text"
                value={formData.parquetPattern || ''}
                onChange={(e) => onFormDataChange({ parquetPattern: e.target.value })}
                placeholder=""
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: theme.text, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.parquetMergeSchema || false}
                onChange={(e) => onFormDataChange({ parquetMergeSchema: e.target.checked })}
              />
              <span style={{ marginLeft: 6 }}>Merge Schema</span>
            </label>
            <div style={{ marginTop: 4, marginLeft: 22, fontSize: 11, color: theme.textSecondary }}>
              Enable schema merging when reading multiple parquet files with different schemas
            </div>
          </div>
        </>
      )}

      {sourceType === 'azure_blob_storage' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Storage Account Name
              </label>
              <input
                type="text"
                value={formData.azureStorageAccountName || ''}
                onChange={(e) => onFormDataChange({ azureStorageAccountName: e.target.value })}
                placeholder="mystorageaccount"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
              <div style={{ marginTop: 4, fontSize: 11, color: theme.textSecondary }}>
                Optional if AZURE_STORAGE_ACCOUNT_NAME env var is set
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Storage Account Key
              </label>
              <input
                type="password"
                value={formData.azureStorageAccountKey || ''}
                onChange={(e) => onFormDataChange({ azureStorageAccountKey: e.target.value })}
                placeholder="base64encodedkey=="
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
              <div style={{ marginTop: 4, fontSize: 11, color: theme.textSecondary }}>
                Optional if AZURE_STORAGE_ACCOUNT_KEY env var is set
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Container *
              </label>
              <input
                type="text"
                value={formData.azureContainer || ''}
                onChange={(e) => onFormDataChange({ azureContainer: e.target.value })}
                placeholder="data-lake"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Path
              </label>
              <input
                type="text"
                value={formData.azurePath || ''}
                onChange={(e) => onFormDataChange({ azurePath: e.target.value })}
                placeholder="raw/customers"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Format *
              </label>
              <select
                value={formData.azureFormat || 'parquet'}
                onChange={(e) => onFormDataChange({ azureFormat: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              >
                <option value="parquet">Parquet</option>
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, marginTop: 28 }}>
              <input
                type="checkbox"
                checked={formData.azureHeader || false}
                onChange={(e) => onFormDataChange({ azureHeader: e.target.checked })}
                disabled={formData.azureFormat !== 'csv'}
              />
              Header row (CSV)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, marginTop: 28 }}>
              <input
                type="checkbox"
                checked={formData.azureInferSchema || false}
                onChange={(e) => onFormDataChange({ azureInferSchema: e.target.checked })}
                disabled={formData.azureFormat !== 'csv'}
              />
              Infer schema (CSV)
            </label>
          </div>

          <div style={{ 
            padding: 12, 
            background: theme.codeBackground, 
            borderRadius: 6, 
            fontSize: 12, 
            color: theme.textSecondary,
            border: `1px solid ${theme.border}`
          }}>
            <strong style={{ color: theme.text }}>Azure Blob Storage Configuration:</strong>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Container and format are required fields</li>
              <li>Storage credentials can be provided here or via environment variables</li>
              <li>Path is relative to the container root (leave empty for root)</li>
              <li>Supported formats: Parquet (default), CSV, JSON</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
